// components/tracking/FeedbackTrigger.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { 
  FaStar, 
  FaTimes, 
  FaPaperPlane, 
  FaThumbsUp, 
  FaComment,
  FaCheck,
  FaHeart,
  FaLightbulb,
  FaShieldAlt
} from 'react-icons/fa';

// Helper function to get detailed device/browser info
const getDeviceInfo = () => {
  const ua = navigator.userAgent;
  
  // Detect OS
  const getOS = () => {
    if (ua.includes('Windows NT 10.0')) return 'Windows 10';
    if (ua.includes('Windows NT 6.3')) return 'Windows 8.1';
    if (ua.includes('Windows NT 6.2')) return 'Windows 8';
    if (ua.includes('Windows NT 6.1')) return 'Windows 7';
    if (ua.includes('Windows')) return 'Windows (Other)';
    if (ua.includes('Mac OS X')) {
      const version = ua.match(/Mac OS X ([0-9_]+)/);
      return version ? `macOS ${version[1].replace(/_/g, '.')}` : 'macOS';
    }
    if (ua.includes('Linux')) return 'Linux';
    if (ua.includes('Android')) {
      const version = ua.match(/Android ([0-9.]+)/);
      return version ? `Android ${version[1]}` : 'Android';
    }
    if (ua.includes('iOS') || ua.includes('iPhone OS')) {
      const version = ua.match(/OS ([0-9_]+)/);
      return version ? `iOS ${version[1].replace(/_/g, '.')}` : 'iOS';
    }
    return 'Unknown OS';
  };

  // Detect Browser
  const getBrowser = () => {
    if (ua.includes('Edg/')) {
      const version = ua.match(/Edg\/([0-9.]+)/);
      return `Microsoft Edge ${version ? version[1] : ''}`;
    }
    if (ua.includes('Chrome/') && !ua.includes('Edg/')) {
      const version = ua.match(/Chrome\/([0-9.]+)/);
      return `Google Chrome ${version ? version[1] : ''}`;
    }
    if (ua.includes('Firefox/')) {
      const version = ua.match(/Firefox\/([0-9.]+)/);
      return `Mozilla Firefox ${version ? version[1] : ''}`;
    }
    if (ua.includes('Safari/') && !ua.includes('Chrome/')) {
      const version = ua.match(/Version\/([0-9.]+)/);
      return `Safari ${version ? version[1] : ''}`;
    }
    if (ua.includes('Opera') || ua.includes('OPR/')) {
      const version = ua.match(/(Opera|OPR)\/([0-9.]+)/);
      return `Opera ${version ? version[2] : ''}`;
    }
    return 'Unknown Browser';
  };

  // Detect Device Type
  const getDeviceType = () => {
    if (/tablet|ipad|playbook|silk/i.test(ua)) return 'Tablet';
    if (/mobile|iphone|ipod|android|blackberry|opera|mini|windows\sce|palm|smartphone|iemobile/i.test(ua)) return 'Mobile';
    return 'Desktop';
  };

  // Get Screen Resolution
  const getScreenInfo = () => {
    return `${window.screen.width}x${window.screen.height}`;
  };

  // Get Timezone
  const getTimezone = () => {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  };

  return {
    os: getOS(),
    browser: getBrowser(),
    device_type: getDeviceType(),
    screen_resolution: getScreenInfo(),
    timezone: getTimezone(),
    language: navigator.language || navigator.userLanguage,
    user_agent: ua,
    platform: navigator.platform,
    cookie_enabled: navigator.cookieEnabled,
    online: navigator.onLine
  };
};

// Function to get user's IP and location
const getLocationInfo = async () => {
  try {
    // Using ipapi.co for IP and location (free service)
    const response = await fetch('https://ipapi.co/json/');
    const data = await response.json();
    
    return {
      ip: data.ip || 'Unknown',
      country: data.country_name || 'Unknown',
      country_code: data.country_code || 'Unknown',
      region: data.region || 'Unknown',
      city: data.city || 'Unknown',
      postal_code: data.postal || 'Unknown',
      latitude: data.latitude || 'Unknown',
      longitude: data.longitude || 'Unknown',
      timezone_location: data.timezone || 'Unknown',
      isp: data.org || 'Unknown'
    };
  } catch (error) {
    console.warn('Could not fetch location info:', error);
    return {
      ip: 'Unknown',
      country: 'Unknown',
      country_code: 'Unknown',
      region: 'Unknown',
      city: 'Unknown',
      postal_code: 'Unknown',
      latitude: 'Unknown',
      longitude: 'Unknown',
      timezone_location: 'Unknown',
      isp: 'Unknown'
    };
  }
};

// Privacy Notice Component
const PrivacyNotice = ({ hasConsent, onConsentChange }) => (
  <div className="text-xs text-gray-500 mt-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
    <div className="flex items-start gap-2">
      <FaShieldAlt className="text-blue-500 mt-0.5 flex-shrink-0" />
      <div>
        <p className="mb-2">
          We collect technical data (device, browser, location) to improve our service and analyze usage patterns.
        </p>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={hasConsent}
            onChange={(e) => onConsentChange(e.target.checked)}
            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <span className="text-xs">I consent to detailed data collection</span>
        </label>
      </div>
    </div>
  </div>
);

const FeedbackSystem = ({ awbNumber, onClose, latestStatus }) => {
  const [step, setStep] = useState(1); // 1: Rating, 2: Detailed Feedback, 3: Thank You
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [feedbackType, setFeedbackType] = useState('');
  const [comment, setComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [hasConsent, setHasConsent] = useState(true);
  const [submissionError, setSubmissionError] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Track if user has provided any meaningful feedback
  const hasMeaningfulFeedback = rating > 0 || comment.trim().length > 0 || feedbackType;

  const feedbackCategories = [
    { id: 'tracking_accuracy', label: 'Tracking Accuracy', icon: '📍' },
    { id: 'update_frequency', label: 'Update Frequency', icon: '🔄' },
    { id: 'website_experience', label: 'Website Experience', icon: '💻' },
    { id: 'delivery_speed', label: 'Delivery Speed', icon: '⚡' },
    { id: 'customer_service', label: 'Customer Service', icon: '👥' },
    { id: 'overall_experience', label: 'Overall Experience', icon: '⭐' }
  ];

  const getRatingText = (rating) => {
    switch (rating) {
      case 1: return 'Very Poor';
      case 2: return 'Poor';
      case 3: return 'Average';
      case 4: return 'Good';
      case 5: return 'Excellent';
      default: return 'Rate your experience';
    }
  };

  const getRatingColor = (rating) => {
    if (rating <= 2) return 'text-red-500';
    if (rating === 3) return 'text-yellow-500';
    return 'text-green-500';
  };

  // Submit partial feedback when closing/minimizing
  const handlePartialSubmit = useCallback(async (reason = 'closed') => {
    if (!hasMeaningfulFeedback) return;

    try {
      const deviceInfo = getDeviceInfo();
      const locationInfo = hasConsent ? await getLocationInfo() : {
        ip: 'Consent not given',
        country: 'Consent not given',
        country_code: 'Consent not given',
        region: 'Consent not given',
        city: 'Consent not given',
        postal_code: 'Consent not given',
        latitude: 'Consent not given',
        longitude: 'Consent not given',
        timezone_location: 'Consent not given',
        isp: 'Consent not given'
      };

      const partialFeedbackData = {
        awb_number: awbNumber,
        rating: rating || 0,
        feedback_type: feedbackType,
        comment: comment.trim() || `Partial feedback (${reason})`,
        current_status: latestStatus?.order_status_description || 'Unknown',
        timestamp: new Date().toISOString(),
        local_time: new Date().toLocaleString(),
        operating_system: deviceInfo.os,
        browser: deviceInfo.browser,
        device_type: deviceInfo.device_type,
        screen_resolution: deviceInfo.screen_resolution,
        platform: deviceInfo.platform,
        language: deviceInfo.language,
        timezone: deviceInfo.timezone,
        user_agent: deviceInfo.user_agent,
        cookie_enabled: deviceInfo.cookie_enabled,
        online_status: deviceInfo.online,
        ip_address: locationInfo.ip,
        country: locationInfo.country,
        country_code: locationInfo.country_code,
        region: locationInfo.region,
        city: locationInfo.city,
        postal_code: locationInfo.postal_code,
        latitude: locationInfo.latitude,
        longitude: locationInfo.longitude,
        timezone_location: locationInfo.timezone_location,
        isp: locationInfo.isp,
        page_url: window.location.href,
        page_title: document.title,
        referrer: document.referrer || 'Direct',
        consent_given: hasConsent,
        submission_type: `partial_${reason}`,
        is_complete: false
      };

      const GOOGLE_FORM_URL = 'https://docs.google.com/forms/d/e/1FAIpQLSeWivZ9n0Oyx-HTLAscY9Ex5QLBaz6c9EHSvl6ocU3YGz7Vow/formResponse';
      const formData = new FormData();

      const feedbackTypeMap = {
        'tracking_accuracy': 'Tracking Accuracy',
        'update_frequency': 'Update Frequency', 
        'website_experience': 'Website Experience',
        'delivery_speed': 'Delivery Speed',
        'customer_service': 'Customer Service',
        'overall_experience': 'Overall Experience'
      };

      // Submit the partial data
      formData.append('entry.764147326', String(partialFeedbackData.awb_number));
      formData.append('entry.1496502242', String(partialFeedbackData.rating));
      
      if (partialFeedbackData.feedback_type) {
        formData.append('entry.1547549376', feedbackTypeMap[partialFeedbackData.feedback_type] || partialFeedbackData.feedback_type);
      }
      formData.append('entry.1051961316', String(partialFeedbackData.comment));
      formData.append('entry.398484877', String(partialFeedbackData.current_status));
      formData.append('entry.1721474206', String(partialFeedbackData.timestamp));
      formData.append('entry.728772882', String(partialFeedbackData.local_time));
      formData.append('entry.1340478562', String(partialFeedbackData.operating_system));
      formData.append('entry.1962170389', String(partialFeedbackData.browser));
      formData.append('entry.537310610', String(partialFeedbackData.device_type));
      formData.append('entry.1944506201', String(partialFeedbackData.screen_resolution));
      formData.append('entry.1538443353', String(partialFeedbackData.language));
      formData.append('entry.446355203', String(partialFeedbackData.timezone));
      formData.append('entry.695229271', String(partialFeedbackData.ip_address));
      formData.append('entry.1773054518', String(partialFeedbackData.country));
      formData.append('entry.503633075', String(partialFeedbackData.region));
      formData.append('entry.2099565503', String(partialFeedbackData.city));
      formData.append('entry.1217926910', String(partialFeedbackData.postal_code));
      formData.append('entry.1588158741', String(partialFeedbackData.isp));
      formData.append('entry.1816095903', String(partialFeedbackData.page_url));
      formData.append('entry.213753517', String(partialFeedbackData.referrer));

      await fetch(GOOGLE_FORM_URL, {
        method: 'POST',
        body: formData,
        mode: 'no-cors'
      });

      console.log(`Partial feedback submitted on ${reason}:`, {
        awb: partialFeedbackData.awb_number,
        rating: partialFeedbackData.rating,
        comment: partialFeedbackData.comment,
        type: partialFeedbackData.feedback_type
      });

      // Clear draft after successful submission
      localStorage.removeItem('feedback_draft');
      setHasUnsavedChanges(false);

    } catch (error) {
      console.error(`Partial feedback submission failed on ${reason}:`, error);
      
      // Store as failed submission for retry
      if (hasConsent) {
        try {
          const failedSubmissions = JSON.parse(localStorage.getItem('failed_feedback_submissions') || '[]');
          failedSubmissions.push({
            feedbackData: {
              awb_number: awbNumber,
              rating,
              feedback_type: feedbackType,
              comment: comment.trim(),
              timestamp: new Date().toISOString(),
              submission_type: `partial_${reason}`
            },
            error: error.message,
            timestamp: new Date().toISOString()
          });
          localStorage.setItem('failed_feedback_submissions', JSON.stringify(failedSubmissions));
        } catch (storageError) {
          console.warn('Could not store failed partial submission:', storageError);
        }
      }
    }
  }, [awbNumber, rating, feedbackType, comment, hasConsent, latestStatus, hasMeaningfulFeedback]);

  // Enhanced close handler with unsaved changes check
  const handleClose = useCallback(async () => {
    if (hasUnsavedChanges) {
      await handlePartialSubmit('closed');
    }
    onClose();
  }, [hasUnsavedChanges, handlePartialSubmit, onClose]);

  // Enhanced minimize handler
  const handleMinimize = useCallback(async () => {
    if (hasUnsavedChanges) {
      await handlePartialSubmit('minimized');
    }
    setIsMinimized(true);
  }, [hasUnsavedChanges, handlePartialSubmit]);

  // Track unsaved changes
  useEffect(() => {
    setHasUnsavedChanges(hasMeaningfulFeedback && step < 3);
  }, [rating, comment, feedbackType, step, hasMeaningfulFeedback]);

  // Auto-save draft feedback periodically
  useEffect(() => {
    if (hasMeaningfulFeedback && hasConsent) {
      const draftData = {
        awb_number: awbNumber,
        rating,
        feedback_type: feedbackType,
        comment: comment.trim(),
        timestamp: new Date().toISOString(),
        is_draft: true
      };
      
      // Save draft to localStorage
      try {
        localStorage.setItem('feedback_draft', JSON.stringify(draftData));
      } catch (error) {
        console.warn('Could not save draft:', error);
      }
    }
  }, [rating, comment, feedbackType, awbNumber, hasMeaningfulFeedback, hasConsent]);

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setSubmissionError(null);
    
    try {
      // Get device and browser information
      const deviceInfo = getDeviceInfo();
      
      // Get location and IP information (only if consent given)
      const locationInfo = hasConsent ? await getLocationInfo() : {
        ip: 'Consent not given',
        country: 'Consent not given',
        country_code: 'Consent not given',
        region: 'Consent not given',
        city: 'Consent not given',
        postal_code: 'Consent not given',
        latitude: 'Consent not given',
        longitude: 'Consent not given',
        timezone_location: 'Consent not given',
        isp: 'Consent not given'
      };
      
      // Prepare comprehensive feedback data
      const feedbackData = {
        // Basic feedback info
        awb_number: awbNumber,
        rating: rating,
        feedback_type: feedbackType,
        comment: comment || 'No comment provided',
        current_status: latestStatus?.order_status_description || 'Unknown',
        
        // Timestamp info
        timestamp: new Date().toISOString(),
        local_time: new Date().toLocaleString(),
        
        // Device & Browser info
        operating_system: deviceInfo.os,
        browser: deviceInfo.browser,
        device_type: deviceInfo.device_type,
        screen_resolution: deviceInfo.screen_resolution,
        platform: deviceInfo.platform,
        language: deviceInfo.language,
        timezone: deviceInfo.timezone,
        user_agent: deviceInfo.user_agent,
        cookie_enabled: deviceInfo.cookie_enabled,
        online_status: deviceInfo.online,
        
        // Location & Network info (based on consent)
        ip_address: locationInfo.ip,
        country: locationInfo.country,
        country_code: locationInfo.country_code,
        region: locationInfo.region,
        city: locationInfo.city,
        postal_code: locationInfo.postal_code,
        latitude: locationInfo.latitude,
        longitude: locationInfo.longitude,
        timezone_location: locationInfo.timezone_location,
        isp: locationInfo.isp,
        
        // Page context
        page_url: window.location.href,
        page_title: document.title,
        referrer: document.referrer || 'Direct',
        
        // Privacy
        consent_given: hasConsent,
        submission_type: 'complete',
        is_complete: true
      };
      
      // Google Forms Integration - CORRECTED VERSION
      const GOOGLE_FORM_URL = 'https://docs.google.com/forms/d/e/1FAIpQLSeWivZ9n0Oyx-HTLAscY9Ex5QLBaz6c9EHSvl6ocU3YGz7Vow/formResponse';
      
      const formData = new FormData();
      
      // Map feedback types to exact Google Form options
      const feedbackTypeMap = {
        'tracking_accuracy': 'Tracking Accuracy',
        'update_frequency': 'Update Frequency', 
        'website_experience': 'Website Experience',
        'delivery_speed': 'Delivery Speed',
        'customer_service': 'Customer Service',
        'overall_experience': 'Overall Experience'
      };
      
      // ✅ CORRECTED FORM FIELD MAPPING
      
      // Required fields
      formData.append('entry.764147326', String(feedbackData.awb_number));        // AWB Number (Required)
      formData.append('entry.1496502242', String(feedbackData.rating));           // Overall Rating (Required)
      
      // Optional feedback fields
      if (feedbackData.feedback_type) {
        formData.append('entry.1547549376', feedbackTypeMap[feedbackData.feedback_type] || feedbackData.feedback_type);
      }
      formData.append('entry.1051961316', String(feedbackData.comment));          // Additional Comments
      formData.append('entry.398484877', String(feedbackData.current_status));    // Current Shipment Status
      
      // Timestamp fields
      formData.append('entry.1721474206', String(feedbackData.timestamp));        // ISO Timestamp
      formData.append('entry.728772882', String(feedbackData.local_time));        // Local Time
      
      // Device & Browser fields
      formData.append('entry.1340478562', String(feedbackData.operating_system)); // Operating System
      formData.append('entry.1962170389', String(feedbackData.browser));          // Browser
      formData.append('entry.537310610', String(feedbackData.device_type));       // Device Type
      formData.append('entry.1944506201', String(feedbackData.screen_resolution));// Screen Resolution
      formData.append('entry.1538443353', String(feedbackData.language));         // Language
      formData.append('entry.446355203', String(feedbackData.timezone));          // Timezone
      
      // Location & Network fields
      formData.append('entry.695229271', String(feedbackData.ip_address));        // IP Address
      formData.append('entry.1773054518', String(feedbackData.country));          // Country
      formData.append('entry.503633075', String(feedbackData.region));            // Region/State
      formData.append('entry.2099565503', String(feedbackData.city));             // City
      formData.append('entry.1217926910', String(feedbackData.postal_code));      // Postal Code
      formData.append('entry.1588158741', String(feedbackData.isp));              // Internet Service Provider
      
      // Page context fields
      formData.append('entry.1816095903', String(feedbackData.page_url));         // Page URL
      formData.append('entry.213753517', String(feedbackData.referrer));          // Referrer
      
      // Debug: Log the form data being sent
      console.log('Submitting feedback data:', {
        awb_number: feedbackData.awb_number,
        rating: feedbackData.rating,
        feedback_type: feedbackData.feedback_type,
        comment: feedbackData.comment,
        consent_given: feedbackData.consent_given
      });
      
      // Try submission with multiple strategies
      let submissionSuccess = false;
      
      // Strategy 1: Try with no-cors (standard for Google Forms)
      try {
        await fetch(GOOGLE_FORM_URL, {
          method: 'POST',
          body: formData,
          mode: 'no-cors'
        });
        submissionSuccess = true;
        console.log('Form submitted successfully with no-cors');
      } catch (noCorsError) {
        console.warn('No-CORS submission failed:', noCorsError);
        
        // Strategy 2: Try with cors to get actual error details
        try {
          const corsResponse = await fetch(GOOGLE_FORM_URL, {
            method: 'POST',
            body: formData,
            mode: 'cors'
          });
          
          if (corsResponse.ok) {
            submissionSuccess = true;
            console.log('Form submitted successfully with CORS');
          } else {
            console.error('CORS submission failed with status:', corsResponse.status, corsResponse.statusText);
            throw new Error(`HTTP ${corsResponse.status}: ${corsResponse.statusText}`);
          }
        } catch (corsError) {
          console.error('CORS submission error:', corsError);
          throw corsError;
        }
      }
      
      // Store submission locally for backup/analytics (only if consent given)
      if (hasConsent) {
        try {
          const submissionRecord = {
            ...feedbackData,
            submission_timestamp: new Date().toISOString(),
            submission_id: `feedback_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            submission_success: submissionSuccess
          };
          
          localStorage.setItem('last_feedback_submission', JSON.stringify(submissionRecord));
          
          // Also add to submissions history
          const history = JSON.parse(localStorage.getItem('feedback_submission_history') || '[]');
          history.push(submissionRecord);
          // Keep only last 10 submissions
          if (history.length > 10) {
            history.splice(0, history.length - 10);
          }
          localStorage.setItem('feedback_submission_history', JSON.stringify(history));
        } catch (storageError) {
          console.warn('Could not store submission locally:', storageError);
        }
      }
      
      // Clear draft after successful submission
      localStorage.removeItem('feedback_draft');
      setHasUnsavedChanges(false);
      setStep(3); // Show success
      
    } catch (error) {
      console.error('Feedback submission failed:', error);
      setSubmissionError(error.message);
      
      // Store failed submission for retry (only if consent given)
      if (hasConsent) {
        try {
          const failedSubmissions = JSON.parse(localStorage.getItem('failed_feedback_submissions') || '[]');
          failedSubmissions.push({
            feedbackData: {
              awb_number: awbNumber,
              rating,
              feedback_type: feedbackType,
              comment,
              timestamp: new Date().toISOString()
            },
            error: error.message,
            timestamp: new Date().toISOString()
          });
          localStorage.setItem('failed_feedback_submissions', JSON.stringify(failedSubmissions));
        } catch (storageError) {
          console.warn('Could not store failed submission:', storageError);
        }
      }
      
      // Still show success to user (Google Forms is hard to verify)
      setStep(3);
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetFeedback = useCallback(() => {
    setStep(1);
    setRating(0);
    setHoverRating(0);
    setFeedbackType('');
    setComment('');
    setIsSubmitting(false);
    setSubmissionError(null);
    setHasUnsavedChanges(false);
    localStorage.removeItem('feedback_draft');
  }, []);

  // Handle escape key to close with unsaved changes check
  useEffect(() => {
    const handleEscape = async (e) => {
      if (e.key === 'Escape') {
        await handleClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [handleClose]);

  // Handle browser/tab close with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = async (e) => {
      if (hasUnsavedChanges) {
        // Submit partial feedback before page unload
        await handlePartialSubmit('page_unload');
        
        // Show browser confirmation dialog
        e.preventDefault();
        e.returnValue = 'You have unsaved feedback. Are you sure you want to leave?';
        return e.returnValue;
      }
    };

    const handleVisibilityChange = async () => {
      if (document.hidden && hasUnsavedChanges) {
        // Page is being hidden (tab switch, minimize browser, etc.)
        await handlePartialSubmit('visibility_change');
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [hasUnsavedChanges, handlePartialSubmit]);

  // Retry failed submissions on mount
  useEffect(() => {
    const retryFailedSubmissions = async () => {
      try {
        const failedSubmissions = JSON.parse(localStorage.getItem('failed_feedback_submissions') || '[]');
        
        if (failedSubmissions.length === 0) return;
        
        console.log(`Found ${failedSubmissions.length} failed submissions. Attempting retry...`);
        
        // Try to resubmit failed submissions (max 3 retries)
        const recentFailures = failedSubmissions.slice(-3);
        
        for (const submission of recentFailures) {
          try {
            // Basic retry attempt
            const formData = new FormData();
            formData.append('entry.764147326', submission.feedbackData.awb_number);
            formData.append('entry.1496502242', String(submission.feedbackData.rating));
            if (submission.feedbackData.feedback_type) {
              formData.append('entry.1547549376', submission.feedbackData.feedback_type);
            }
            formData.append('entry.1051961316', submission.feedbackData.comment);
            
            await fetch('https://docs.google.com/forms/d/e/1FAIpQLSeWivZ9n0Oyx-HTLAscY9Ex5QLBaz6c9EHSvl6ocU3YGz7Vow/formResponse', {
              method: 'POST',
              body: formData,
              mode: 'no-cors'
            });
            
            console.log('Retry successful for submission:', submission.feedbackData.awb_number);
          } catch (retryError) {
            console.warn('Retry failed:', retryError);
          }
        }
        
        // Clear failed submissions after retry attempt
        localStorage.removeItem('failed_feedback_submissions');
      } catch (error) {
        console.error('Error during retry:', error);
      }
    };

    retryFailedSubmissions();
  }, []);

  if (isMinimized) {
    return (
      <div className="fixed bottom-4 right-4 z-50">
        <button
          onClick={() => setIsMinimized(false)}
          className={`bg-blue-600 text-white p-3 rounded-full shadow-lg hover:bg-blue-700 transition-all duration-300 hover:scale-110 group relative ${
            hasUnsavedChanges ? 'ring-2 ring-yellow-400 ring-offset-2' : ''
          }`}
          aria-label="Open feedback form"
        >
          <FaComment className="text-lg group-hover:animate-pulse" />
          
          {/* Unsaved changes indicator */}
          {hasUnsavedChanges && (
            <div className="absolute -top-1 -right-1 w-3 h-3 bg-yellow-400 rounded-full animate-pulse border-2 border-white"></div>
          )}
        </button>
        
        {/* Enhanced tooltip */}
        <div className="absolute bottom-full right-0 mb-3 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none">
          {hasUnsavedChanges ? 'Continue feedback (unsaved changes)' : 'Continue feedback'}
          <div className="absolute top-full right-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
        </div>
      </div>
    );
  }

  return (
    <div id="feedback-widget" className="fixed bottom-4 right-4 z-50">
      <div className="bg-white rounded-xl shadow-2xl border border-gray-200 w-80 max-h-[600px] overflow-hidden animate-in slide-in-from-right-5 duration-300">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FaHeart className="text-pink-300 animate-pulse" />
              <h3 className="font-semibold text-sm">
                {step === 3 ? 'Thank You!' : 'Share Your Feedback'}
              </h3>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handleMinimize}
                className="text-white/80 hover:text-white text-lg transition-colors p-1 rounded hover:bg-white/20"
                aria-label="Minimize feedback"
              >
                −
              </button>
              <button
                onClick={handleClose}
                className="text-white/80 hover:text-white transition-colors p-1 rounded hover:bg-white/20"
                aria-label="Close feedback"
              >
                <FaTimes className="text-sm" />
              </button>
            </div>
          </div>
          {step < 3 && (
            <p className="text-xs text-blue-100 mt-1">
              Help us improve your tracking experience
              {hasUnsavedChanges && (
                <span className="ml-2 inline-flex items-center gap-1">
                  <span className="w-1.5 h-1.5 bg-yellow-300 rounded-full animate-pulse"></span>
                  Unsaved changes
                </span>
              )}
            </p>
          )}
        </div>

        <div className="p-4 max-h-[500px] overflow-y-auto">
          {/* Step 1: Rating */}
          {step === 1 && (
            <div className="text-center space-y-4">
              <div className="flex items-center justify-center gap-2 mb-4">
                <FaLightbulb className="text-blue-500" />
                <p className="text-sm text-gray-700 font-medium">
                  How would you rate your tracking experience?
                </p>
              </div>
              
              {/* Star Rating */}
              <div className="flex justify-center gap-2 mb-4">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    onMouseEnter={() => setHoverRating(star)}
                    onMouseLeave={() => setHoverRating(0)}
                    onClick={() => setRating(star)}
                    className="text-3xl transition-all duration-200 hover:scale-125 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded"
                    aria-label={`Rate ${star} star${star !== 1 ? 's' : ''}`}
                  >
                    <FaStar 
                      className={`transition-colors duration-200 ${
                        star <= (hoverRating || rating)
                          ? 'text-yellow-400 drop-shadow-sm'
                          : 'text-gray-300'
                      }`}
                    />
                  </button>
                ))}
              </div>
              
              {/* Rating Text */}
              <p className={`text-base font-semibold transition-colors duration-200 ${getRatingColor(rating || hoverRating)}`}>
                {getRatingText(rating || hoverRating)}
              </p>
              
              {/* Quick Actions for High Ratings */}
              {rating >= 4 && (
                <div className="flex gap-2 justify-center">
                  <button
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                    className="flex items-center gap-2 px-4 py-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors text-sm font-medium border border-green-200 disabled:opacity-50"
                  >
                    {isSubmitting ? (
                      <div className="w-3 h-3 border-2 border-green-600 border-t-transparent rounded-full animate-spin"></div>
                    ) : (
                      <FaThumbsUp className="text-sm" />
                    )}
                    {isSubmitting ? 'Submitting...' : "That's all, thanks!"}
                  </button>
                  <button
                    onClick={() => setStep(2)}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors text-sm font-medium border border-blue-200"
                  >
                    <FaComment className="text-sm" />
                    Add details
                  </button>
                </div>
              )}
              
              {/* Continue Button for Low Ratings */}
              {rating > 0 && rating < 4 && (
                <button
                  onClick={() => setStep(2)}
                  className="w-full bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 transition-colors text-sm font-semibold"
                >
                  Continue
                </button>
              )}
            </div>
          )}

          {/* Step 2: Detailed Feedback */}
          {step === 2 && (
            <div className="space-y-4">
              <div className="flex items-center gap-3 pb-3 border-b border-gray-100">
                <div className="flex">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <FaStar 
                      key={star}
                      className={`text-lg ${star <= rating ? 'text-yellow-400' : 'text-gray-300'}`}
                    />
                  ))}
                </div>
                <span className={`text-sm font-medium ${getRatingColor(rating)}`}>
                  {getRatingText(rating)}
                </span>
              </div>
              
              {/* Feedback Categories */}
              <div>
                <p className="text-sm text-gray-700 mb-3 font-medium">
                  What specifically would you like to comment on?
                </p>
                
                <div className="grid grid-cols-2 gap-2 mb-4">
                  {feedbackCategories.map((category) => (
                    <button
                      key={category.id}
                      onClick={() => setFeedbackType(category.id)}
                      className={`p-3 text-xs rounded-lg border-2 transition-all duration-200 ${
                        feedbackType === category.id
                          ? 'bg-blue-50 border-blue-300 text-blue-700 shadow-sm'
                          : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex flex-col items-center gap-1">
                        <span className="text-lg">{category.icon}</span>
                        <span className="font-medium leading-tight text-center">{category.label}</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Comment Box */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tell us more about your experience
                </label>
                <textarea
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder={`Share your thoughts about ${feedbackType ? feedbackCategories.find(c => c.id === feedbackType)?.label.toLowerCase() : 'your experience'}...`}
                  className="w-full p-3 border-2 border-gray-200 rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  rows={3}
                  maxLength={500}
                />
                <div className="text-xs text-gray-500 mt-1 text-right">
                  {comment.length}/500 characters
                </div>
              </div>
              
              {/* Privacy Notice */}
              <PrivacyNotice hasConsent={hasConsent} onConsentChange={setHasConsent} />
              
              {/* Submission Error Display */}
              {submissionError && (
                <div className="bg-red-50 border-2 border-red-200 rounded-lg p-3">
                  <p className="text-sm text-red-700">
                    ⚠️ Submission error: {submissionError}
                  </p>
                  <p className="text-xs text-red-600 mt-1">
                    Don't worry, your feedback has been saved locally and we'll retry automatically.
                  </p>
                </div>
              )}
              
              {/* Action Buttons */}
              <div className="flex gap-2 pt-2">
                <button
                  onClick={() => setStep(1)}
                  className="flex-1 py-2 px-4 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium"
                >
                  Back
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  className="flex-1 py-2 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm font-semibold flex items-center justify-center gap-2"
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Sending...
                    </>
                  ) : (
                    <>
                      <FaPaperPlane className="text-xs" />
                      Submit Feedback
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Thank You */}
          {step === 3 && (
            <div className="text-center py-2 space-y-4">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto animate-in zoom-in-50 duration-500">
                <FaCheck className="text-green-600 text-3xl" />
              </div>
              
              <div>
                <h3 className="font-bold text-lg text-gray-800 mb-2">
                  Thank you for your feedback!
                </h3>
                
                <p className="text-sm text-gray-600 leading-relaxed">
                  Your input helps us improve our tracking service. We appreciate you taking the time to share your experience.
                </p>
              </div>
              
              {rating >= 4 && (
                <div className="bg-green-50 border-2 border-green-200 rounded-lg p-4">
                  <p className="text-sm text-green-700 font-medium">
                    🎉 We're thrilled you had a great experience! Consider sharing your positive experience with others.
                  </p>
                </div>
              )}
              
              {rating <= 3 && rating > 0 && (
                <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4">
                  <p className="text-sm text-blue-700 font-medium">
                    We're sorry your experience wasn't perfect. Our team will review your feedback to make improvements.
                  </p>
                </div>
              )}
              
              {submissionError && (
                <div className="bg-yellow-50 border-2 border-yellow-200 rounded-lg p-4">
                  <p className="text-sm text-yellow-700 font-medium">
                    📝 Your feedback was saved locally and will be submitted automatically when possible.
                  </p>
                </div>
              )}
              
              {/* Action Buttons */}
              <div className="flex gap-2 pt-2">
                <button
                  onClick={resetFeedback}
                  className="flex-1 py-2 px-4 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium"
                >
                  Give More Feedback
                </button>
                <button
                  onClick={handleClose}
                  className="flex-1 py-2 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-semibold"
                >
                  Close
                </button>
              </div>
            </div>
          )}
        </div>
        
        {/* Progress Indicator */}
        {step < 3 && (
          <div className="bg-gray-50 px-4 py-3 border-t border-gray-100">
            <div className="flex justify-center gap-2">
              {[1, 2].map((stepNum) => (
                <div
                  key={stepNum}
                  className={`w-3 h-3 rounded-full transition-all duration-300 ${
                    step >= stepNum ? 'bg-blue-600 scale-110' : 'bg-gray-300'
                  }`}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Enhanced Feedback Widget Trigger Component
const FeedbackTrigger = ({ awbNumber, latestStatus }) => {
  const [showFeedback, setShowFeedback] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  // Smart auto-show logic based on delivery status
  useEffect(() => {
    const shouldAutoShow = () => {
      if (hasInteracted) return false;
      
      // Show feedback for delivered packages after shorter delay
      if (latestStatus?.order_status_code === '100' || 
          latestStatus?.order_status_description?.toLowerCase().includes('delivered')) {
        return 5000; // 5 seconds for delivered packages
      }
      
      // Show for other statuses after longer delay
      return 15000; // 15 seconds for other statuses
    };

    const delay = shouldAutoShow();
    if (!delay) return;

    const timer = setTimeout(() => {
      setIsVisible(true);
    }, delay);

    return () => clearTimeout(timer);
  }, [hasInteracted, latestStatus]);

  // Show trigger after visibility delay
  useEffect(() => {
    if (isVisible) {
      const timer = setTimeout(() => {
        if (!hasInteracted) {
          setShowFeedback(true);
        }
      }, 2000); // Additional 2 seconds after becoming visible

      return () => clearTimeout(timer);
    }
  }, [isVisible, hasInteracted]);

  // Check for pending offline submissions on mount
  useEffect(() => {
    const checkPendingSubmissions = () => {
      const pending = localStorage.getItem('failed_feedback_submissions');
      if (pending) {
        try {
          const submissions = JSON.parse(pending);
          if (submissions.length > 0) {
            console.log(`Found ${submissions.length} pending feedback submissions`);
          }
        } catch (error) {
          console.warn('Error checking pending submissions:', error);
        }
      }
    };

    checkPendingSubmissions();
    
    // Also check when coming back online
    const handleOnline = () => {
      console.log('Back online - checking pending submissions');
      checkPendingSubmissions();
    };
    
    window.addEventListener('online', handleOnline);
    return () => window.removeEventListener('online', handleOnline);
  }, []);

  const handleShowFeedback = useCallback(() => {
    setShowFeedback(true);
    setHasInteracted(true);
  }, []);

  const handleCloseFeedback = useCallback(() => {
    setShowFeedback(false);
    setHasInteracted(true);
  }, []);

  // Don't show anything if not visible yet
  if (!isVisible && !showFeedback) {
    return null;
  }

  return (
    <>
      {!showFeedback && (
        <div className="fixed bottom-4 right-4 z-40">
          <div className="relative group">
            <button
              onClick={handleShowFeedback}
              className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-4 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-110 animate-pulse hover:animate-none focus:outline-none focus:ring-4 focus:ring-blue-300"
              aria-label="Open feedback form"
            >
              <FaComment className="text-xl" />
            </button>
            
            {/* Enhanced tooltip */}
            <div className="absolute bottom-full right-0 mb-3 px-4 py-2 bg-gray-900 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-all duration-200 whitespace-nowrap pointer-events-none transform group-hover:translate-y-[-2px]">
              Share your feedback
              <div className="absolute top-full right-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
            </div>
          </div>
        </div>
      )}
      
      {showFeedback && (
        <FeedbackSystem 
          awbNumber={awbNumber}
          latestStatus={latestStatus}
          onClose={handleCloseFeedback}
        />
      )}
    </>
  );
};

// Utility functions for managing feedback data
export const FeedbackUtils = {
  // Get all feedback submission history
  getSubmissionHistory: () => {
    try {
      return JSON.parse(localStorage.getItem('feedback_submission_history') || '[]');
    } catch (error) {
      console.warn('Error reading submission history:', error);
      return [];
    }
  },
  
  // Get failed submissions that need retry
  getFailedSubmissions: () => {
    try {
      return JSON.parse(localStorage.getItem('failed_feedback_submissions') || '[]');
    } catch (error) {
      console.warn('Error reading failed submissions:', error);
      return [];
    }
  },
  
  // Clear all stored feedback data
  clearAllData: () => {
    localStorage.removeItem('feedback_submission_history');
    localStorage.removeItem('failed_feedback_submissions');
    localStorage.removeItem('last_feedback_submission');
    localStorage.removeItem('feedback_draft');
    console.log('All feedback data cleared');
  },
  
  // Export feedback data for analysis
  exportData: () => {
    const data = {
      history: FeedbackUtils.getSubmissionHistory(),
      failed: FeedbackUtils.getFailedSubmissions(),
      last: localStorage.getItem('last_feedback_submission'),
      draft: localStorage.getItem('feedback_draft')
    };
    
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `feedback-data-${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }
};

export default FeedbackTrigger;
export { FeedbackSystem };